<template>
  <div>
    <div class="tariff-data">
      <template v-for="(meterpointTariff, index) in filteredMeterpointTariffs">
        <div
          v-if="meterpointTariff.publiclyAvailableTariffList?.length"
          :key="index"
        >
          <label class="contact-details__inline-input">
            <span v-if="!meterpointTariff.meterpoint.isElectricity">
              Gas Tariff ({{ meterpointTariff.meterpoint.identifier }})
            </span>
            <span v-else>
              Electricity Tariff ({{ meterpointTariff.meterpoint.identifier }})
            </span>
            <SoSelect
              v-model="
                tariffs[
                  meterpointTariff.meterpoint.isElectricity
                    ? 'electricity'
                    : 'gas'
                ]
              "
              :items="
                getSelectTariffs(meterpointTariff.publiclyAvailableTariffList)
              "
              @update:model-value="updateField"
            />
          </label>

          <SoAlert v-if="!meterpointTariff.isAllTariffsAvailable" type="info">
            <p>
              Not all tariffs are available for the Date of Responsibility
              selected
            </p>
            <template #expandable>
              <div class="mt-2">
                <strong>Available tariffs:</strong>
                <ul class="bulletpoint-list">
                  <li
                    v-for="tariff in meterpointTariff.publiclyAvailableTariffList"
                    :key="tariff.code"
                  >
                    {{ tariff.display }}
                  </li>
                </ul>
              </div>
            </template>
          </SoAlert>
        </div>
      </template>
    </div>

    <SoButton
      title="Generate a Quote"
      type="secondary"
      icon="external-link sm"
      large
      class="mt-6"
      @click="openTariffQuoter()"
    />
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { ITariff } from "@/models/tariff/tariff.types"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { RouteNames } from "@/types/router"
  import { TariffAgreementsWithMeterpoint } from "@/types/MeterpointTariffs"
  import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
  import dayjs from "dayjs"

  dayjs.extend(isSameOrAfter)

  type MeterpointTariffsFiltered = {
    [meterpointId: string]: TariffAgreementsWithMeterpoint & {
      isAllTariffsAvailable: boolean
    }
  }

  export default defineComponent({
    components: {
      SoButton,
      SoSelect,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Object as PropType<Record<string, string>>,
        default: () => ({}),
      },
      dateOfResponsibility: {
        type: [Object, String] as PropType<Date | string>,
        default: null,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        tariffs: {} as Record<string, string>,
      }
    },
    computed: {
      meterpointTariffs() {
        return this.current.meterpointTariffs.data
      },
      filteredMeterpointTariffs() {
        return Object.keys(
          this.meterpointTariffs,
        ).reduce<MeterpointTariffsFiltered>((prev, current) => {
          const currentMeterpointTariff = this.meterpointTariffs[current]
          const publicTariffs =
            currentMeterpointTariff.publiclyAvailableTariffList ?? []
          const filteredTariffs = publicTariffs.filter((tariff) =>
            dayjs(this.dateOfResponsibility).isSameOrAfter(
              dayjs(tariff.productDetails.fromDttm),
            ),
          )

          return {
            ...prev,
            [current]: {
              ...currentMeterpointTariff,
              publiclyAvailableTariffList: filteredTariffs,
              isAllTariffsAvailable:
                publicTariffs.length === filteredTariffs.length,
            },
          }
        }, {})
      },
    },
    methods: {
      updateField() {
        this.$emit("update:modelValue", this.tariffs)
      },
      getSelectTariffs(tariffs: ITariff[]) {
        return (
          tariffs?.map((tariff) => ({
            title: tariff.display,
            value: `${tariff.code}`,
          })) ?? []
        )
      },
      openTariffQuoter() {
        const route = this.$router.resolve({ name: RouteNames.TariffQuoter })
        window.open(route.href, "_blank")
      },
    },
  })
</script>

<style scoped>
  .tariff-data {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
</style>
